import {defineStore} from 'pinia'
import {
    ValueDriver,
    Variable,
    PriceComponent,
    Meta,
    WidgetValue,
    GlobalCalculations,
    VariableUpdate,
    Choice,
    Benefit,
    Feature
} from "@lpiadmin/giving-tree/dist";
import {getValuePropositionAPI} from "@/services/value-proposition-api";

export const useGivingTreeStore = defineStore('givingTreeStore', {
    state: () => ({
        globalCalculations: {} as GlobalCalculations,
        metaByHookId: {} as { [key: string]: Meta },
        valueDriversByHookId: {} as { [key: string]: ValueDriver },
        variablesByHookId: {} as { [key: string]: Variable },
        priceComponentsByHookId: {} as { [key: string]: PriceComponent },
        choice: {} as Choice,
        updated: false,
        benefits: {} as { [key: string]: Benefit },
        features: {} as { [key: string]: Feature },
    }),

    getters: {
        getWidgetValue(state) {
            return (hookId: string): WidgetValue => {
                if (state.benefits.hasOwnProperty(hookId)) {
                    return state.benefits[hookId]
                }

                if (state.metaByHookId.hasOwnProperty(hookId)) {
                    return state.metaByHookId[hookId]
                }

                if (state.globalCalculations.hasOwnProperty(hookId)) {
                    return state.globalCalculations[hookId]
                }

                if (state.valueDriversByHookId.hasOwnProperty(hookId)) {
                    return state.valueDriversByHookId[hookId]
                }

                if (state.variablesByHookId.hasOwnProperty(hookId)) {
                    return state.variablesByHookId[hookId]
                }

                if (state.priceComponentsByHookId.hasOwnProperty(hookId)) {
                    return state.priceComponentsByHookId[hookId]
                }

            }
        }
    },
    actions: {
        setWidgetValue(hookId: string, value: any) {
            isDirty = true
            if (this.metaByHookId[hookId]) {
                this.metaByHookId[hookId].textValue = value
                fireApiGlobalUpdate()
            }

            if (this.globalCalculations[hookId]) {
                this.globalCalculations[hookId].value = parseFloat(value)
            }

            if (this.valueDriversByHookId[hookId]) {
                this.valueDriversByHookId[hookId].value = parseFloat(value)
            }

            if (this.variablesByHookId[hookId]) {
                this.variablesByHookId[hookId].value = value === null || value === '' ? null : parseFloat(value)
            }

            if (this.priceComponentsByHookId[hookId]) {
                this.priceComponentsByHookId[hookId].value = parseFloat(value)
            }
        },

        syncVariable(hookId: string, value: VariableUpdate) {
            isDirty = true
            if (hookId && !this.variablesByHookId[hookId].hasFormula) {
                this.variablesByHookId[hookId].value = value
            }
        },

        syncMetaFromApi(field: string, value: string) {
            if (field) {
                isDirty = true
                this.metaByHookId[getMetaHookId(field)].textValue = value
            }
        },

        syncVariableFromApi(hookId: string, value: number) {
            if (hookId) {
                isDirty = true
                const converted = this.variablesByHookId[hookId].variableType === "percent" ? value * 100 : value
                if (this.variablesByHookId[hookId].value !== converted) {
                    this.variablesByHookId[hookId].value = converted
                }
            }
        },
        sync(
            variables: Variable[],
            valueDrivers: ValueDriver[],
            priceComponents: PriceComponent[],
            globalCalculations: GlobalCalculations) {

            isDirty = true
            this.updated = true

            for (const valueDriver of valueDrivers) {
                this.valueDriversByHookId[valueDriver.hookId].value = valueDriver.value
            }

            for (const priceComponent of priceComponents) {
                this.priceComponentsByHookId[priceComponent.hookId].value = priceComponent.value
            }

            for (const variable of variables) {
                if (variable?.hookId && this.variablesByHookId[variable?.hookId]) {
                    this.variablesByHookId[variable.hookId].notes = variable.notes
                    this.variablesByHookId[variable.hookId].value = variable.value
                    this.variablesByHookId[variable.hookId].overrideValue = variable.value
                }
            }

            for (const prop in globalCalculations) {
                if (this.globalCalculations.hasOwnProperty(prop)) {
                    const value = globalCalculations[prop].value
                    // TODO this needs to get better!

                    // a little hacky - we're actually not setting a new value, just need pinia to know it did get set before
                    this.globalCalculations[prop].value = 0
                    this.globalCalculations[prop].value = value
                }
            }
        }
    }
})


let isDirty = false

/*
TODO this should be in the main stuff but not the calculator
window.addEventListener("beforeunload", (event) => {
    if (isDirty) {
        event.preventDefault();
        event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
    }
})
*/
document.addEventListener("save-complete", async function (event: any) {
    isDirty = false
})


function getMetaHookId(field: string) {
    switch (field) {
        case "Name":
            return "meta-name"
        case "Company":
            return "meta-customer"
        case "Offering":
            return "meta-offering"
        case "Competitor":
            return "meta-competitor"
        case "Description":
            return "meta-description"
        case "CompetitorNotes":
            return "meta-competitor-notes"
    }
    return ""
}

function fireApiGlobalUpdate() {
    debounce(() => {
        const api = getValuePropositionAPI()
        if (api) {
            const api = getValuePropositionAPI()
            if (api) {
                api.refreshMeta()
            }
        }
    }, 1000)
}

let timeoutId: number | null = null


function debounce(callback: () => void, delay: number) {
    if (timeoutId) {
        clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(callback, delay)
}
